import { useState } from "react";
import { X, Download, ZoomIn, ZoomOut, FileText } from "lucide-react";
import { Button } from "./ui/button";
import { BACKEND_URL, authFetch } from "../App";

const DocumentPreview = ({ fileUrl, filename, contentType, onClose }) => {
  const [zoom, setZoom] = useState(1);
  
  if (!fileUrl) return null;

  const fullUrl = fileUrl.startsWith("http") ? fileUrl : `${BACKEND_URL}${fileUrl}`;
  // For auth, we'll construct the URL with the token as query param for iframe/img src
  const token = localStorage.getItem("session_token");
  const authedUrl = `${fullUrl}${fullUrl.includes("?") ? "&" : "?"}token=${token}`;
  
  const isPdf = contentType?.includes("pdf") || filename?.toLowerCase().endsWith(".pdf");
  const isImage = contentType?.includes("image") || /\.(jpg|jpeg|png|webp)$/i.test(filename || "");

  const handleDownload = async () => {
    try {
      const res = await authFetch(fullUrl);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "document";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      window.open(fullUrl, "_blank");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.8)" }}
      onClick={onClose}
      data-testid="document-preview-modal"
    >
      <div
        className="relative w-full max-w-5xl h-[85vh] mx-4 rounded-lg overflow-hidden flex flex-col"
        style={{ background: "var(--bg-secondary)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-3 border-b shrink-0"
          style={{ borderColor: "var(--border-color)" }}
        >
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="w-4 h-4 shrink-0 text-[var(--accent)]" />
            <span className="text-sm font-medium truncate" style={{ color: "var(--text-primary)" }}>
              {filename || "Document Preview"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {isImage && (
              <>
                <Button variant="ghost" size="sm" onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}>
                  <ZoomOut className="w-4 h-4" />
                </Button>
                <span className="text-xs" style={{ color: "var(--text-muted)" }}>{Math.round(zoom * 100)}%</span>
                <Button variant="ghost" size="sm" onClick={() => setZoom((z) => Math.min(3, z + 0.25))}>
                  <ZoomIn className="w-4 h-4" />
                </Button>
              </>
            )}
            <Button variant="ghost" size="sm" onClick={handleDownload}>
              <Download className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto flex items-center justify-center p-4">
          {isPdf ? (
            <iframe
              src={authedUrl}
              className="w-full h-full rounded border-0"
              title="Document Preview"
            />
          ) : isImage ? (
            <img
              src={authedUrl}
              alt={filename}
              className="max-w-full max-h-full object-contain rounded transition-transform duration-200"
              style={{ transform: `scale(${zoom})` }}
            />
          ) : (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 mx-auto mb-3" style={{ color: "var(--text-muted)" }} />
              <p style={{ color: "var(--text-secondary)" }}>Preview not available for this file type</p>
              <Button className="mt-4" onClick={handleDownload}>Download File</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentPreview;
